import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useHistory } from "react-router-dom";
import { useForm as useReactForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { Modal, OverlayTrigger } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";
import { format as dateFormat, add as dateAdd } from "date-fns";
import { isAxiosError } from "axios";

import axios from "axios";
import fileDownload from "js-file-download";
import OrderHistoryTable from "./OrderHistoryTable";
// import OrderHistorySearchSummary from "./OrderHistorySearchSummary";
import { apiUrl, basePath } from "../../../../config";
import {
  getDeliveryHistory,
  getExportOrderHistory,
  voidOrder as voidOrderEndpoint,
  getLabelsForMultipleDeliveries,
  printManifest,
  simplifiedManifest,
  getDeliveriesByBarcodes,
  getDeliveriesByInvoices,
} from "../../../../services/deliveryService";
import handleApiError from "../../../../helpers/handleApiError";
import useForm from "../../../../hooks/useForm";

import PolarQuestionModal from "./PolarQuestionModal";
import { defaultTitle, tokenKeyName } from "../../../../config";
import DialogModal from "../../../DialogModal";

// import context from "react-bootstrap/esm/AccordionContext";
// import download from "downloadjs";
// import { OrderHistoryCXTStatusTest } from "./Orders/OrderHistoryCXTStatusTest";
// import { OrderDetailsPanel } from "./Orders/OrderDetailsPanel";
import { isClient } from "../../../../services/accountService";
import { DiliTextCP } from "../../../Utility/DiliTextCP";
import {
  convertArrayToGetParamsArrayString,
  openInNewTab,
  // sleep,
} from "../../../../helpers/utilities";
import { BsInfoCircle } from "react-icons/bs";
import OrderHistorySearchFormV2NoSwitch from "../../../OrderHistorySearchFormV2NoSwitch";
import { getCdkImportedOrders } from "../../../../services/cdkOrderService";
import {
  localStorageVariables,
  orderHistoryPreFilters,
  orderHistoryVars,
  orderSearchCategories,
  pages,
  emails,
} from "../../../../constants";
import jwtDecode from "jwt-decode";
import { cn } from "../../../../helpers/cn";
import LoadingModal from "../../../LoadingModal";
import { TooltipStandard } from "../../../Utility/TooltipStandard";
import { CDKAlertNote2 } from "../../../CDK/CDKSignupPage/CDKAlertNote2";
import { RestrictedContent } from "../../../Navbar/RestrictedContent";

const BatchPrintModal = ({
  show,
  handleCloseModal,
  handleBatchPrint,
  // setLoading,
  history,
}) => {
  const [typedAgreement, setTypedAgreement] = useState("");

  const handleChangeTypedAgreement = (e) => {
    setTypedAgreement(e.target.value);
  };

  //reset typed agreement whenver modal is closed
  useEffect(() => {
    if (!show) {
      setTypedAgreement("");
    }
  }, [show]);

  const validatedHandleBatchPrint = async (e) => {
    e.preventDefault();
    if (typedAgreement === "YES") {
      // setLoading(true);
      try {
        await handleBatchPrint(e);
        // setLoading(false);
      } catch (ex) {
        // setLoading(false);
        handleApiError(ex, history);
      }
    } else {
      toast.error(
        "Please type YES into the text field if you would like to batch print."
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      // dialogClassName="signature-modal"
    >
      <Modal.Header>
        <Modal.Title>Batch Print</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Batch print all labels for the current date range. <br />
          Diligent is not responsible for lost packages due to mislabeling by
          client. Print at your own risk. <br />
          Are you sure you wish to proceed? <br />
          Type YES to initiate batch print. This may take a few moments.
        </p>
        <DiliTextCP
          autocomplete="off"
          name="typedAgreement"
          value={typedAgreement}
          validationTest={(input) => {
            return input === "YES";
          }}
          placeholder="Type YES here if you would like to proceed."
          onChange={handleChangeTypedAgreement}
        />
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-primary"
          type="button"
          onClick={validatedHandleBatchPrint}
        >
          Batch Print
        </button>
        <button
          className="btn btn-secondary"
          type="button"
          onClick={handleCloseModal}
        >
          Cancel
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const printManifestOptions = [
  {
    value: "manifest",
    label: "Manifest",
  },
  {
    value: "simplifiedManifest",
    label: "Simplified Manifest",
  },
];

/**
 * @typedef {Object} submitManifestData
 * @property {string} manifestDatePrint
 * @property {boolean} printAlreadyPrinted
 * @property {string} printManifestType
 */

const onSuccessfulPrintManifest = (response, variables) => {
  const dateParts = variables.manifestDatePrint.split("-");
  const [year, month, day] = dateParts;

  fileDownload(
    response?.data,
    `Diligent Manifest for ${dateFormat(
      new Date(year, month - 1, day),
      "MM-dd-yyyy"
    )}.pdf`
  );
};

const onErrorPrintManifest = (history) => (error, variables) => {
  if (variables?.printAlreadyPrinted) {
    handleApiError(
      error,
      history,
      true,
      "Error downloading Manifest. You may not have any orders for the selected date."
    );
  }

  if (!variables?.printAlreadyPrinted) {
    handleApiError(
      error,
      history,
      true,
      "Error downloading Manifest. You may not have any unprinted orders for the selected date."
    );
  }
};

const PrintManifestModalAdv = forwardRef((_, ref) => {
  const history = useHistory();
  const { mutate: mutatePrintManifest, isLoading: isLoadingPrintManifestData } =
    useMutation({
      mutationFn: ({ manifestDatePrint, printAlreadyPrinted }) =>
        printManifest(manifestDatePrint, printAlreadyPrinted),

      onSuccess: onSuccessfulPrintManifest,

      onError: onErrorPrintManifest(history),
    });

  const {
    mutate: mutateSimplifiedManifest,
    isLoading: isLoadingSimplifiedManifestData,
  } = useMutation({
    mutationFn: ({ manifestDatePrint, printAlreadyPrinted }) =>
      simplifiedManifest(manifestDatePrint, printAlreadyPrinted),

    onSuccess: onSuccessfulPrintManifest,

    onError: onErrorPrintManifest(history),
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useReactForm({
    defaultValues: {
      manifestDatePrint: dateFormat(new Date(), "yyyy-MM-dd"),
      printAlreadyPrinted: false,
      printManifestType: "manifest",
    },
  });

  const [show, setShow] = useState(false);

  const handleClose = () => {
    setShow(false);
    reset();
  };

  /**
   * @param {submitManifestData} data
   */
  const onSubmit = (data) => {
    if (data.printManifestType === "manifest") {
      mutatePrintManifest({
        manifestDatePrint: data.manifestDatePrint,
        printAlreadyPrinted: data.printAlreadyPrinted,
      });
    }

    if (data.printManifestType === "simplifiedManifest") {
      mutateSimplifiedManifest({
        manifestDatePrint: data.manifestDatePrint,
        printAlreadyPrinted: data.printAlreadyPrinted,
      });
    }

    handleClose();
  };

  useImperativeHandle(ref, () => ({
    showPrintManifestModal: () => {
      setShow(true);
    },
  }));

  return (
    <>
      <LoadingModal
        active={isLoadingPrintManifestData || isLoadingSimplifiedManifestData}
      />
      <DialogModal
        show={show}
        title="Print Manifest"
        onHide={handleClose}
        cancelButtonProps={{ cancelText: "Close", onClick: handleClose }}
        saveButtonProps={{ className: "tw-hidden", onClick: undefined }}
      >
        <form
          className="tw-text-center tw-space-y-4 tw-mb-3 tw-mt-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div>
            <input
              type="date"
              className={cn(
                "form-control",
                errors.manifestDatePrint
                  ? "tw-border-red-500 hover:tw-border-red-500 focus-within:tw-border-red-500"
                  : ""
              )}
              {...register("manifestDatePrint", { required: true })}
              max={dateFormat(new Date(), "yyyy-MM-dd")}
            />

            {errors.manifestDatePrint && (
              <small className="tw-text-red-500">
                Please enter a valid date.
              </small>
            )}
          </div>

          <div>
            <select
              className={cn(
                "form-control",
                errors.printManifestType
                  ? "tw-border-red-500 hover:tw-border-red-500 focus-within:tw-border-red-500"
                  : ""
              )}
              {...register("printManifestType", { required: true })}
            >
              <option disabled value="">
                Select Manifest Type
              </option>

              {printManifestOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {errors.printManifestType && (
              <small className="tw-text-red-500">
                Please select a manifest type.
              </small>
            )}
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              {...register("printAlreadyPrinted")}
            />

            <label className="form-check-label">
              Include already printed orders?
            </label>

            <OverlayTrigger
              key="top"
              placement="top"
              flip
              overlay={
                <TooltipStandard id={`tooltip-top`}>
                  By default, the manifest will only include orders that have
                  not already been printed on a previous manifest. If you would
                  like the manifest to include all orders regardless of whether
                  they have been on a previous manifest or not, please check
                  this box.
                </TooltipStandard>
              }
            >
              <div className="tw-inline">
                <BsInfoCircle className="info-circle-color" />
              </div>
            </OverlayTrigger>
          </div>

          <button type="submit" className="btn btn-primary">
            Print
          </button>
        </form>
      </DialogModal>
    </>
  );
});

PrintManifestModalAdv.displayName = "PrintManifestModalAdv";

const OrderHistoryBoard = ({
  setLoading,
  decodedClientInfo,
  mode,
  onChangeMode,
}) => {
  const [searchFormValues, setSearchFormValues] = useForm({
    fromDate: dateFormat(
      dateAdd(new Date(), { days: orderHistoryVars.defaultFromOffset }),
      "yyyy-MM-dd"
    ),
    toDate: dateFormat(
      dateAdd(new Date(), { days: orderHistoryVars.defaultToOffset }),
      "yyyy-MM-dd"
    ),
  });

  const [currentSearchRange, setCurrentSearchRange] = useState({
    fromDate: dateFormat(
      dateAdd(new Date(), { days: orderHistoryVars.defaultFromOffset }),
      "yyyy-MM-dd"
    ),
    toDate: dateFormat(
      dateAdd(new Date(), { days: orderHistoryVars.defaultToOffset }),
      "yyyy-MM-dd"
    ),
  });
  const [expandRowEvent, setExpandRowEvent] = useState(0); //used to expand first row when searching by invoice/barcode
  // const [searchByBarcode, setSearchByBarcode] = useState(false); // was for when we had a switch between search by date range and barcode
  // useEffect(() => {
  //   if (barcodeSearch === true) {
  //     const response = axios.get(`${apiUrl}Delivery/GetBarcode`);
  //     console.log(response);
  //   }
  // }, [barcodeSearch]);

  const [, setDeliveryId] = useState([]);
  const [, setInvoiceNumber] = useState([]);
  // const [customer, setCustomer] = useState([]);
  // const [route, setCustomerRoute] = useState([]);
  // const [cod, setCod] = useState([]);
  // const [status, setStatus] = useState([]);
  const [orders, setOrders] = useState([]);
  // const [cdkOrderLUT, setCdkOrderLUT] = useState([]); //a list of delivery ids that are CDK orders we can use to filter for CDK orders.
  //Instead I'm just going to use that LUT temporarily to save the cdkorders themselves into state
  const [cdkOrders, setCDKOrders] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState(false);
  const [showPolarQuestionModal, setShowPolarQuestionModal] = useState(false);
  // const [, setHover] = useState(false);
  // const onHover = () => {
  //   setHover(true);
  // };
  // const onHoverLeave = () => {
  //   setHover(false);
  // };
  const [polarQuestionModalState, setPolarQuestionModalState] = useState({
    title: "Uninitialized",
    message: "Uninitialized",
    onYesClick: () => {},
  });

  const history = useHistory();

  /**
   * @type {React.MutableRefObject<{showPrintManifestModal: function():void} | null>} dialogModalRef
   */
  const printManifestModalRef = useRef(null);

  const processOrderData = useCallback((foundOrders) => {
    // console.log("orders:");
    let ordersGroupedByTranAndLinkIds = {};
    // //lets make an array with just the main invoice orders first
    // console.log("maininvoiceorders:");
    // // const mainInvoiceOrders = foundOrders.filter((order) => {
    // //   return order.linkId === 0; //main invoice orders have a linkid of 0
    // // });
    // //need to filter AND map invoice string to an array so we can push into it later, so let's do both at once in our own for loop instead of two array functions
    for (let i = 0; i < foundOrders.length; i++) {
      if (foundOrders[i].linkId === 0) {
        let orderToAdd = foundOrders[i];
        //     //we can keep the original main invoice number and cod amount in the invoiceNumber field and codAmount field
        //     //so making new array fields
        orderToAdd.subInvoices = [];
        orderToAdd.subAmounts = [];
        orderToAdd.subInvoicesInsured = [];
        ordersGroupedByTranAndLinkIds[foundOrders[i].deliveryId] =
          foundOrders[i];
      }
    }
    // //now loop again, this time adding the subinvoices invoice numbers and cod amounts to the fields we made before
    for (let i = 0; i < foundOrders.length; i++) {
      if (foundOrders[i].linkId !== 0) {
        // console.log(foundOrders[i].linkId);
        //Known Issue:
        //added question marks as if there was a redelivery, we can potentially have subinvoices that are linked to orders outside of date range
        //as redeliveries currently only update the delivery date (deltrans tdate) for the main invoice.
        //example:
        //1: order with 3 subinvoices was supposed to be delivered on 11/1/2022.
        //2: Diligent employee schedules redelivery for 11/2/2022
        //3: The main invoice deltrans entry will have a tdate of 11/2/2022
        //but the child invoices will still have a tdate of 11/1/2022. (we haven't worked on w.e. applications can schedule redeliveries)
        //4: Client searches for orders being delivered on 11/1/2022
        //5: The subinvoice orders come through but not the main order
        //6: our ordersGroupedByTranAndLinkIds does not have the main order as a key because the client was not looking for orders supposed to be delivered 11/2/2022, only 11/1/2022
        //7: When we try to access the key we get undefined and we cannot access subInvoices of undefined, so it will error out.
        //Putting the question mark makes it so it will not run and just be omitted from the results.
        ordersGroupedByTranAndLinkIds[foundOrders[i].linkId]?.subInvoices.push(
          foundOrders[i].invoiceNumber
        );
        ordersGroupedByTranAndLinkIds[foundOrders[i].linkId]?.subAmounts.push(
          foundOrders[i].amount
        );
        ordersGroupedByTranAndLinkIds[
          foundOrders[i].linkId
        ]?.subInvoicesInsured.push(foundOrders[i].insured);
      }
    }

    let ordersGroupedByTranAndLinkIdsArray = [];
    //can also prob use object.entries/values methods instead of for in
    for (const deliveryId in ordersGroupedByTranAndLinkIds) {
      ordersGroupedByTranAndLinkIdsArray.push(
        ordersGroupedByTranAndLinkIds[deliveryId]
      );
    }

    // console.log(
    //   "Orders with grouped invoices and cod amounts based on tran/link id:"
    // );
    // console.log(ordersGroupedByTranAndLinkIds);
    // console.log(ordersGroupedByTranAndLinkIdsArray);
    // }
    // console.log(mainInvoiceOrders);
    //commented for push

    //now lets push
    // for (let i = 0; i < foundOrders.length; i++) {
    //   if(foundOrders[i].linkId === 0){
    //     ordersGroupedByTranAndLinkIds[foundOrders.deliveryId] = foundOrders[i]
    //   }
    //   if (foundOrders[i].linkId) {
    //     console.log(foundOrders[i].linkId);
    //     ordersGroupedByTranAndLinkIds
    //   }
    // }
    // console.log(foundOrders);
    // setOrders(foundOrders);
    return ordersGroupedByTranAndLinkIdsArray;
  }, []);

  const processCDKOrders = useCallback(
    async (fromDate, toDate, foundOrders) => {
      // console.log(foundOrders);
      // console.log(toDate);
      // console.log(fromDate);
      //need a from date and to date to get a list of delivery ids for orders that were imported using the cdk order import page.
      //then we filter the actual order objects we found based on that list to get a list of order info for the cdk orders only.
      const { data: cdkOrdersResponseData } = await getCdkImportedOrders(
        fromDate,
        dateAdd(new Date(toDate), { days: 1 })
      );

      const cdkProcessedOrders = [...foundOrders];

      //while we don't really use wasCDKImportedOrder yet, as we store the cdk orders in their own state CDKOrders for filtering
      //we still need to set their subinvoices in the normal orders state variable.
      for (let i = 0; i < cdkOrdersResponseData.length; i++) {
        const cdkOrder = cdkOrdersResponseData[i];
        for (let j = 0; j < foundOrders.length; j++) {
          const order = foundOrders[j];
          if (order.deliveryId === cdkOrder.deliveryId) {
            order.wasCDKImportedOrder = true;
            // console.log(cdkOrder.invoiceNumberList);
            //Sometimes the invoiceNumber list also contains the main invoice number, sometimes it doesn't, so let's make sure we don't pass it in.
            order.subInvoices = cdkOrder.invoiceNumberList.filter((invoice) => {
              return invoice !== order.invoiceNumber;
            });
          }
        }
      }
      setCDKOrders(
        foundOrders.filter((order) => {
          return cdkOrdersResponseData.some((cdkOrderResponseDataObject) => {
            return cdkOrderResponseDataObject.deliveryId === order.deliveryId;
          });
        })
      );

      return cdkProcessedOrders;
    },
    []
  );
  // console.log("orders state", orders);
  useEffect(() => {
    // console.log(searchFormValues);
    let mounted = true; //to prevent state updates after unmount

    const formatDeliveryList = (deliveryList, returnRequestList) => {
      if (!deliveryList || (deliveryList && !returnRequestList)) {
        return [];
      }

      // Noted there allow for multiple return requests per delivery

      const deliveryListWithReturnRequests = deliveryList.map((delivery) => {
        const returnRequests = returnRequestList.filter(
          (returnRequest) => returnRequest.deliveryId === delivery.deliveryId
        );

        if (returnRequests.length > 0) {
          const returnRequestStatuses = {
            // isReturnRequestedApproved: returnRequests.some(
            //   (returnRequest) => returnRequest.isImported
            // ),
            // isReturnRequestedDenied: returnRequests.some(
            //   (returnRequest) => returnRequest.isDenied
            // ),
            isReturnRequestedPending: returnRequests.some(
              (returnRequest) =>
                !returnRequest?.isImported && !returnRequest?.isDenied
            ),
          };

          return {
            ...delivery,
            returnRequestStatuses,
          };
        }

        return delivery;
      });

      return deliveryListWithReturnRequests;
    };

    (async () => {
      const defaultFromDate = dateFormat(
        dateAdd(new Date(), { days: orderHistoryVars.defaultFromOffset }),
        "yyyy-MM-dd"
      );
      const defaultToDate = dateFormat(
        dateAdd(new Date(), { days: orderHistoryVars.defaultToOffset }),
        "yyyy-MM-dd"
      );

      // console.log("should set loading to true");
      // setLoading(true);
      setOrderHistoryLoading(true);
      setOrderHistoryError(false);

      try {
        const responseData = await getDeliveryHistory(
          defaultFromDate,
          defaultToDate,
          isClient()
        );

        if (!mounted) {
          return;
        }

        const { deliveryList, returnRequestList } = responseData?.data;

        let foundOrders = formatDeliveryList(deliveryList, returnRequestList);

        // console.log(foundOrders);
        // console.log(returnRequestList);

        const ordersGroupedByTranAndLinkIdsArray =
          processOrderData(foundOrders);
        // console.log(ordersGroupedByTranAndLinkIdsArray);

        //For CDK Clients we want to cache a list of which of their orders were cdk/not cdk
        //the decodedClientInfo object we pass to this page as a prop is not ready by the time this useEffect is run so we get it here manually.
        const isCDKClient = jwtDecode(
          localStorage.getItem(tokenKeyName)
        ).IsCdkClient;

        if (isCDKClient) {
          foundOrders = await processCDKOrders(
            defaultFromDate,
            defaultToDate,
            foundOrders
          );
        }

        // console.log("found orders after CDK processing", foundOrders);

        setOrders(ordersGroupedByTranAndLinkIdsArray);

        // setLoading(false);
        setOrderHistoryLoading(false);
      } catch (ex) {
        // setLoading(false);

        let errorMessage;

        if (isAxiosError(ex)) {
          const { message, code, status, response } = ex;
          const { data, status: responseStatus } = response || {};

          // errorMessage = `Axios Error - ${[code, name, status, message].filter(Boolean).join(", ")}`;

          const errorMessagJOIN = [code, status, message]
            .filter(Boolean)
            .join(", ")
            .trim();
          const responseMessageJOIN = [responseStatus, data?.message]
            .filter(Boolean)
            .join(", ")
            .trim();

          if (errorMessagJOIN !== "") {
            errorMessage = `${errorMessagJOIN}`;
          }
          if (responseMessageJOIN !== "") {
            // errorMessage += ` Response: ${responseMessageJOIN}`;
            // We only want to show either the errorMessage or the responseMessage, not both
            errorMessage = ` Response: ${responseMessageJOIN}`; //fixed bug where errorMessage could be undefined here
          }
        } else {
          errorMessage =
            ex?.response?.data?.message || ex?.message || ex || true;
        }

        setOrderHistoryLoading(false);
        setOrderHistoryError(errorMessage);
        handleApiError(ex, history);
      }
    })();

    return () => {
      mounted = false; //cleanup function to prevent state updates after unmount
    };
  }, [
    history,
    //  setLoading
  ]);

  const handleSearchSubmit = async (searchType, selections) => {
    const formatDeliveryList = (deliveryList, returnRequestList) => {
      if (!deliveryList || (deliveryList && !returnRequestList)) {
        return [];
      }

      // Noted there allow for multiple return requests per delivery

      const deliveryListWithReturnRequests = deliveryList.map((delivery) => {
        const returnRequests = returnRequestList.filter(
          (returnRequest) => returnRequest.deliveryId === delivery.deliveryId
        );

        if (returnRequests.length > 0) {
          const returnRequestStatuses = {
            // isReturnRequestedApproved: returnRequests.some(
            //   (returnRequest) => returnRequest.isImported
            // ),
            // isReturnRequestedDenied: returnRequests.some(
            //   (returnRequest) => returnRequest.isDenied
            // ),
            isReturnRequestedPending: returnRequests.some(
              (returnRequest) =>
                !returnRequest?.isImported && !returnRequest?.isDenied
            ),
          };

          return {
            ...delivery,
            returnRequestStatuses,
          };
        }

        return delivery;
      });

      return deliveryListWithReturnRequests;
    };

    if (
      searchType === orderSearchCategories.barcode.value ||
      searchType === orderSearchCategories.invoice.value
    ) {
      setOrderHistoryLoading(true);
      setOrderHistoryError(false);
      try {
        let deliveriesByReferenceSearchResponse = [];
        if (searchType === orderSearchCategories.barcode.value) {
          deliveriesByReferenceSearchResponse = await getDeliveriesByBarcodes(
            convertArrayToGetParamsArrayString(selections, "barcode")
          );
        } else if (searchType === orderSearchCategories.invoice.value) {
          deliveriesByReferenceSearchResponse = await getDeliveriesByInvoices(
            convertArrayToGetParamsArrayString(selections, "invoiceNumber")
          );
        }
        const deliveriesByReferenceSearchGrouped = processOrderData(
          deliveriesByReferenceSearchResponse.data
        );
        setOrders(deliveriesByReferenceSearchGrouped);
        setOrderHistoryLoading(false);
        setExpandRowEvent((prev) => prev + 1);
      } catch (ex) {
        let errorMessage;

        if (isAxiosError(ex)) {
          const { message, code, status, response } = ex;
          const { data, status: responseStatus } = response || {};

          // errorMessage = `Axios Error - ${[code, name, status, message].filter(Boolean).join(", ")}`;

          const errorMessagJOIN = [code, status, message]
            .filter(Boolean)
            .join(", ")
            .trim();
          const responseMessageJOIN = [responseStatus, data?.message]
            .filter(Boolean)
            .join(", ")
            .trim();

          if (errorMessagJOIN !== "") {
            errorMessage = `${errorMessagJOIN}`;
          }
          if (responseMessageJOIN !== "") {
            errorMessage = ` Response: ${responseMessageJOIN}`;
          }
        } else {
          errorMessage =
            ex?.response?.data?.message || ex?.message || ex || true;
        }

        setOrderHistoryLoading(false);
        setOrderHistoryError(errorMessage);

        // handleApiError(ex, history);
        if (ex.message === "Network Error") {
          handleApiError(ex, history);
        } else if (ex?.response?.status === 401) {
          handleApiError(ex, history);
          // console.log("Found Orders by Barcode:");
        } else {
          if (
            ex?.response?.data?.message === "Sequence contains no elements" ||
            ex?.response?.data?.message ===
              "No orders found for the specified invoices."
          )
            toast.error(
              `No orders found for the specified ${searchType?.toLowerCase()}s.`
            );
          else if (
            ex?.response?.data?.message ===
            "Input string was not in a correct format."
          ) {
            toast.error(
              `There was an issue processing your search. Please make sure you have the correct ${searchType?.toLowerCase()}s. If the issue persists, please contact ${
                emails.supportEmail
              }.`
            );
          } else {
            toast.error("There was an issue processing your search.");
          }
        }
      }
    } else if (searchType === orderSearchCategories.dateRange.value) {
      setOrderHistoryLoading(true);
      setOrderHistoryError(false);
      try {
        const responseData = await getDeliveryHistory(
          searchFormValues.fromDate,
          searchFormValues.toDate,
          isClient()
        );

        const { deliveryList, returnRequestList } = responseData?.data;

        let foundOrders = formatDeliveryList(deliveryList, returnRequestList);

        //For CDK Clients we want to cache a list of which of their orders were cdk/not cdk
        //at this point the decoded client object we pass as a prop has been set and we do not need to grab the token manually to check.
        if (decodedClientInfo.IsCdkClient) {
          await processCDKOrders(
            searchFormValues.fromDate,
            searchFormValues.toDate,
            foundOrders
          );
        }

        const ordersGroupedByTranAndLinkIdsArray =
          processOrderData(foundOrders);
        setOrders(ordersGroupedByTranAndLinkIdsArray);

        //this is used as part of the logic in determining whether the batch print button should be disabled or not.
        setCurrentSearchRange({
          fromDate: searchFormValues.fromDate,
          toDate: searchFormValues.toDate,
        });

        setOrderHistoryLoading(false);
      } catch (ex) {
        let errorMessage;

        if (isAxiosError(ex)) {
          const { message, code, status, response } = ex;
          const { data, status: responseStatus } = response || {};

          // errorMessage = `Axios Error - ${[code, name, status, message].filter(Boolean).join(", ")}`;

          const errorMessagJOIN = [code, status, message]
            .filter(Boolean)
            .join(", ")
            .trim();
          const responseMessageJOIN = [responseStatus, data?.message]
            .filter(Boolean)
            .join(", ")
            .trim();

          if (errorMessagJOIN !== "") {
            errorMessage = `${errorMessagJOIN}`;
          }
          if (responseMessageJOIN !== "") {
            errorMessage = ` Response: ${responseMessageJOIN}`;
          }
        } else {
          errorMessage =
            ex?.response?.data?.message || ex?.message || ex || true;
        }

        setOrderHistoryLoading(false);
        setOrderHistoryError(errorMessage);

        handleApiError(ex, history);
      }
    }
  };
  const [showBatchPrintModal, setShowBatchPrintModal] = useState(false);
  const openBatchPrintModal = () => {
    setShowBatchPrintModal(true);
  };
  const closeBatchPrintModal = () => {
    setShowBatchPrintModal(false);
  };
  const handleBatchPrint = useCallback(
    async (e) => {
      await (async () => {
        const { data: foundOrders } = await getDeliveryHistory(
          searchFormValues.fromDate,
          searchFormValues.toDate,
          isClient()
        );
        let result = foundOrders.map((item) => item.deliveryId);
        // console.log(searchFormValues.fromDate);
        // console.log(searchFormValues.toDate);
        // console.log(result);
        // let placeholder = result.toString();
        // console.log(toilet);
        // const object = [
        //   {
        //     deliveryId: placeholder,
        //     isBatch: batch,
        //   },
        // ];

        // none of the set loading stuff seems to be working here but i moved it up into the validatedHandleBatchPrint in the batchprintmodal for now
        setLoading(true);
        // console.log("loading true");
        axios
          .post(
            `${apiUrl}Delivery/GetLabelsForMultipleDeliveries/`,
            { deliveryId: result.toString(), isBatch: true, isClient: true },

            {
              responseType: "arraybuffer",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/pdf",
              },
            }
          )
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              "Batch Print for " +
                searchFormValues.fromDate +
                " to " +
                searchFormValues.toDate +
                " printed on " +
                dateFormat(new Date(), "MM-dd-yyyy") +
                ".pdf"
            ); //or any other extension
            document.body.appendChild(link);
            link.click();
          })
          .then(() => {
            setLoading(false);
            // console.log("loading false");
            setShowBatchPrintModal(false);
            toast.success("Batch printing has finished.");
          })
          .catch((error) => {
            // console.log(error);
            setLoading(false);
            handleApiError(error, history);
            toast.error("There was an error while batch printing.");
          });

        // await sleep(5000); //for testing
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement("a");
        // link.href = url;
        // link.setAttribute("download", "myFile.pdf");
        // document.body.appendChild(link);
        // link.click();
        // console.log(response);

        e.preventDefault();

        // setLoading(true);

        // try {
        // console.log(foundOrders);

        // At this point it should prompt the user to download the xlsx file
        // setLoading(false);
        // } catch (ex) {
        // setLoading(false);
        // handleApiError(ex, history);
        // }
      })();
    },
    [setLoading, searchFormValues]
  );

  const handleExportClick = useCallback(
    (e) => {
      (async () => {
        e.preventDefault();

        setLoading(true);

        try {
          const { data } = await getExportOrderHistory(
            searchFormValues.fromDate,
            searchFormValues.toDate
          );
          // console.log(data);

          // At this point it should prompt the user to download the xlsx file
          fileDownload(data, "OrderHistory.xlsx");
          setLoading(false);
        } catch (ex) {
          setLoading(false);
          handleApiError(ex, history);
        }
      })();
    },
    [history, setLoading, searchFormValues.fromDate, searchFormValues.toDate]
  );

  const handleModifyClick = useCallback(
    (e) => {
      const {
        deliveryId,
        // linkId
      } = e.target.dataset;
      // history.push(`/modify-order/${linkId > 0 ? linkId : deliveryId}`); //the linkid check was to redirect to the main order as if the linkid is >0 it is a reference to the main order, and we are looking at a subinvoice order. However, we updated the table to group all the subinvoices into the main order, so it is no longer needed.
      // history.push(`/modify-order/${deliveryId}`);
      // window.open(
      //   `${basePath}/modify-order/${deliveryId}`,
      //   "_blank",
      //   "noreferrer noopener"
      // );
      // console.log("setting deliveryID: ", deliveryId);
      localStorage.setItem(localStorageVariables.lastModifying, deliveryId);
      openInNewTab(`${basePath}/modify-order/${deliveryId}`);
    },
    [history]
  );

  const voidOrder = useCallback(
    (deliveryId) => {
      (async () => {
        setLoading(true);

        try {
          const response = await voidOrderEndpoint(deliveryId);
          // console.log(response);
          // console.log(response.data);
          const deletedIds = response.data;
          // console.log(orders);
          // console.log(deliveryId);
          setOrders(
            orders.filter((order) => {
              return !deletedIds.includes(order.deliveryId);
            })
          );
          // console.log(
          //   orders.filter((order) => {
          //     return !deletedIds.includes(order.deliveryId);
          //   })
          // );

          toast.success(`Voided order ${deliveryId}`);
          setLoading(false);
        } catch (ex) {
          // console.log("error - so don't filter");
          setLoading(false);
          handleApiError(ex, history);
        } finally {
          setPolarQuestionModalState({
            title: "Uninitialized",
            message: "Uninitialized",
            onYesClick: () => {},
          });
        }
      })();
    },
    [history, setLoading, orders]
  );

  const handleVoidClick = useCallback(
    (e) => {
      e.preventDefault();

      const { deliveryId } = e.target.dataset;

      setPolarQuestionModalState({
        title: "Confirm Void",
        message: "Are you sure you want to void this order?",
        onYesClick: async () => {
          e.preventDefault();
          setShowPolarQuestionModal(false);
          voidOrder(deliveryId);
        },
      });

      setShowPolarQuestionModal(true);
    },
    [voidOrder]
  );

  const handlePrintLabelClick = useCallback(
    (e) => {
      (async () => {
        e.preventDefault();

        const { deliveryId } = e.target.dataset;

        setLoading(true);
        try {
          // const { data } = await getLabelsForMultipleDeliveries([deliveryId]);
          const { data } = await getLabelsForMultipleDeliveries(
            "" + deliveryId,
            false,
            isClient()
          );
          //at this point it should prompt the user to download
          // console.log(data);
          fileDownload(data, `Label-${deliveryId}.pdf`); //For downloading, but changed it so the label would open in a new tab. Might want to make this a setting in the profile page. TODO
          // openInNewTab(`${basePath}/view-labels/${"" + deliveryId}`);
          setLoading(false);
        } catch (ex) {
          setLoading(false);
          handleApiError(ex, history);
        }
      })();
    },
    [history, setLoading]
  );

  const handleReturnRequestClick = useCallback(
    (e) => {
      // console.log(e);

      e.preventDefault();
      const { deliveryId } = e.target.dataset;

      // history.push(`/post-return/${deliveryId}`);
      // history.push(`/ActionCenter/Return/${deliveryId}`);
      // window.open(
      //   `${basePath}/ActionCenter/Return/${deliveryId}`,
      //   "_blank",
      //   "noreferrer noopener"
      // );
      openInNewTab(`${basePath}/ActionCenter/Return/${deliveryId}`);
    },
    [history]
  );

  const handleDeliveryConcernRequestClick = useCallback(
    (e) => {
      e.preventDefault();

      const {
        deliveryId,
        // invoiceNumber,
        // creationDate
      } = e.target.dataset;

      // history.push(
      //   `/post-delivery-concern/${deliveryId}?invoiceNumber=${invoiceNumber}&creationDate=${creationDate}`
      // );
      // history.push(`/ActionCenter/DeliveryConcern/${deliveryId}`);
      openInNewTab(`${basePath}/ActionCenter/DeliveryConcern/${deliveryId}`);
    },
    [history]
  );
  const handleClickViewConcern = useCallback((e) => {
    e.preventDefault();
    const { concernIncidentNo } = e.target.dataset;
    openInNewTab(`${basePath}${pages.viewConcern.url}/${concernIncidentNo}`);
  }, []);
  const handleClickViewClaim = useCallback((e) => {
    e.preventDefault();
    const { claimId } = e.target.dataset;
    openInNewTab(`${basePath}${pages.viewClaim.url}/${claimId}`);
  }, []);
  const handleSearchSave = useCallback(
    (e) => {
      e.preventDefault();
      const { deliveryId, invoiceNumber, creationDate } = e.target.dataset;
      setDeliveryId(deliveryId);
      setInvoiceNumber(invoiceNumber);
      // console.log(e);
    },
    [history]
  );
  const handlePostClaimClick = useCallback(
    (e) => {
      e.preventDefault();

      const { deliveryId } = e.target.dataset;

      // history.push(`/post-claim/${deliveryId}`);
      // history.push(`/ActionCenter/Claim/${deliveryId}`);
      openInNewTab(`${basePath}/ActionCenter/Claim/${deliveryId}`);
    },
    [history]
  );

  //Print manifest stuff
  // const [, setShowPrintManifestModal] = useState(false);
  // const handleOpenPrintManifestModal = () => {
  //   setShowPrintManifestModal(true);
  // };
  // const handleClosePrintManifestModal = () => {
  //   setShowPrintManifestModal(false);
  // };

  // const handlePrintManifestButtonClick = async () => {
  //   // console.log("print manifest");
  //   // // console.log(new Date());
  //   // console.log(dateFormat(new Date(), "yyyy-MM-dd"));
  //   // const response = await printManifest("2022-01-31");
  //   // console.log(response);
  //   // const data = response.data;
  //   // fileDownload(data, `Manifest_Test.pdf`);
  //   handleOpenPrintManifestModal();
  // };

  // const handlePrintManifest = async (date, printAlreadyPrinted) => {
  //   // console.log("print manifest");
  //   // // console.log(new Date());
  //   // console.log(dateFormat(new Date(), "yyyy-MM-dd"));
  //   // console.log(printAlreadyPrinted);
  //   try {
  //     setLoading(true);
  //     const response = await printManifest(date, printAlreadyPrinted);
  //     const dateParts = date.split("-");
  //     const year = dateParts[0];
  //     const month = dateParts[1];
  //     const day = dateParts[2];

  //     const data = response.data;
  //     // console.log("what?");
  //     // console.log(date);
  //     // console.log(new Date(date))
  //     // console.log(new Date(year, month - 1, day));
  //     fileDownload(
  //       data,
  //       `Diligent Manifest for ${dateFormat(
  //         new Date(year, month - 1, day),
  //         "MM-dd-yyyy"
  //       )}.pdf`
  //     );
  //     setLoading(false);
  //   } catch (ex) {
  //     setLoading(false);
  //     //maybe add custom error messages or change api response codes to not found or no content or w.e. as our test client just gets generic message

  //     if (printAlreadyPrinted) {
  //       handleApiError(
  //         ex,
  //         history,
  //         true,
  //         "Error downloading Manifest. You may not have any orders for the selected date."
  //       );
  //     } else {
  //       handleApiError(
  //         ex,
  //         history,
  //         true,
  //         "Error downloading Manifest. You may not have any unprinted orders for the selected date."
  //       );
  //     }
  //   }
  //   // console.log(response);
  // };

  const [preFilter, setPreFilter] = useState(orderHistoryPreFilters.All);
  const handleChangePreFilter = useCallback((e) => {
    setPreFilter(e.target.value);
  }, []);

  return (
    <div>
      <Helmet>
        <title>Order History - {defaultTitle}</title>
      </Helmet>
      {/* <OrderHistoryLoadingModal orderHistoryLoading={orderHistoryLoading} /> */}

      <PolarQuestionModal
        title={polarQuestionModalState.title}
        message={polarQuestionModalState.message}
        onYesClick={polarQuestionModalState.onYesClick}
        onCloseClick={() => setShowPolarQuestionModal(false)}
        active={showPolarQuestionModal}
      />
      <PrintManifestModalAdv ref={printManifestModalRef} />
      <BatchPrintModal
        show={showBatchPrintModal}
        handleCloseModal={closeBatchPrintModal}
        handleBatchPrint={handleBatchPrint}
        setLoading={setLoading}
        history={history}
      />
      <div className="tw-relative">
        <h1 className="mb-4 tw-text-center">Order History</h1>
        <RestrictedContent>
          {!isClient() && (
            <div className="mx-auto mb-4" style={{ maxWidth: "1200px" }}>
              <CDKAlertNote2 message="If your order still says open after a while and you need to make a return, please use the manual return option found in the navigation bar above." />
            </div>
          )}
        </RestrictedContent>
        <OrderHistorySearchFormV2NoSwitch
          formValues={searchFormValues}
          currentSearchRange={currentSearchRange}
          setFormValues={setSearchFormValues}
          handleExportClick={handleExportClick}
          handleSearch={handleSearchSubmit}
          handleOpenBatchPrint={openBatchPrintModal}
          handlePrintManifestClick={() => {
            printManifestModalRef.current?.showPrintManifestModal();
          }}
          decodedClientInfo={decodedClientInfo}
          orderCount={orders.length}
          mode={mode}
          onChangeMode={onChangeMode}
          isLoading={orderHistoryLoading}
          // searchByBarcode={searchByBarcode}
          // setSearchByBarcode={setSearchByBarcode}
        />
        {/* <OrderHistorySearchSummary
          fromDate={searchFormValues.fromDate}
          toDate={searchFormValues.toDate}
          deliveryId={deliveryId}
          invoiceNumber={invoiceNumber}
          customer={customer}
          route={route}
          cod={cod}
          status={status}
        /> */}
      </div>
      {/* <OrderHistoryCXTStatusTest
        fromDate={searchFormValues.fromDate}
        toDate={searchFormValues.toDate}
        orders={orders}
      /> */}
      <OrderHistoryTable
        orders={preFilter === orderHistoryPreFilters.CDK ? cdkOrders : orders}
        onModifyClick={handleModifyClick}
        onVoidClick={handleVoidClick}
        onPrintLabelClick={handlePrintLabelClick}
        onReturnRequestClick={handleReturnRequestClick}
        onDeliveryConcernRequestClick={handleDeliveryConcernRequestClick}
        handleClickViewConcern={handleClickViewConcern}
        handleClickViewClaim={handleClickViewClaim}
        onPostClaimClick={handlePostClaimClick}
        decodedClientInfo={decodedClientInfo}
        handleSearchSave={handleSearchSave}
        handleChangePreFilter={handleChangePreFilter}
        preFilter={preFilter}
        expandRowEvent={expandRowEvent}
        loading={orderHistoryLoading}
        error={orderHistoryError}
      />
    </div>
  );
};

export default OrderHistoryBoard;
